<template>
  <div
    class="w-full h-full model-view"
    v-loading="loadingEngine"
    :class="modelWidth === 1 / 2 ? 'w-1/2' : 'w-full'"
  >
    <img
      id="playerEngineId"
      class="engineImg w-full"
      v-if="modelSetting.cloudRenderLoadMode === 'img'"
    />
    <video id="playerEngineId" autoplay playsinline muted class="engineVideo w-full" v-else></video>
    <!-- 模型进度条 -->
    <player-progress
      :loadType="loadType"
      :percentage="loadPercentage"
      v-if="showLoadProgress"
    ></player-progress>
  </div>
</template>

<script>
import { get, max, filter, last } from 'lodash'
import dayjs from 'dayjs'
import {
  generateGUID,
  getPreferenceSettings,
  downloadFile,
} from '../../assets/js/preview/publicMethods'
import preloadService from '../../service/PreloadService'

const PlayerProgress = () => {
  return import('./PlayerProgress')
}
export default {
  name: 'ModelPreview',
  components: { PlayerProgress },
  props: {
    cloudRender: {
      type: Object,
      default: () => {},
    },
    modelWidth: {
      type: Number,
      defalut: 1, // 模型的宽度
    },
    modelDetail: {
      type: Object,
      defalut: () => {
        return {
          filePath: '',
          materialFileId: '',
          modelFileId: '', // 模型文件id，云渲染路径又是模型加载名称
        }
      },
    },
  },
  data() {
    return {
      player: null, // player实例
      loadingEngine: false, // 引擎加载状态
      showLoadProgress: false, // 进度条显隐
      loadType: '', // 进度条类型
      sceneRootNode: 0, // 根节点
      loadPercentage: 0, // 进度条值
      modelEngineId: '',
      preloadEngine: null,
    }
  },
  created() {
    this.loadingEngine = true
  },
  computed: {
    // 模型相关设置
    modelSetting() {
      return Wiz.globalSettings.globalConfig?.modelSetting || {}
    },
    // 云渲染协议
    cloudProtocol() {
      return this.modelSetting.cloudProtocol || 'ws'
    },
    // 云渲染服务路径
    cloudServerPath() {
      return this.modelSetting.cloudServerPath || '192.168.20.79:8082'
    },
  },
  watch: {},
  async mounted() {
    // 添加预加载配置则优先考虑预加载
    if (this.modelSetting.preloadServer && this.modelDetail.modelFileId) {
      this.getDistributeEngine()
    } else {
      this.runPlayer()
    }
    window.addEventListener('beforeunload', async (e) => {
      if (this.modelSetting.preloadServer) {
        this.forceTerminate(this.preloadEngine || this.modelEngineId)
      } else if (this.player) {
        this.player.exit()
      }
    })
  },
  beforeDestroy() {
    if (this.modelSetting.preloadServer) {
      this.forceTerminate(this.preloadEngine || this.modelEngineId)
    } else if (this.player) {
      this.player.exit()
    }
  },
  methods: {
    // 获取预加载分配节点
    async getDistributeEngine() {
      try {
        const [err, res] = await preloadService.getEngineId({
          MD5: this.modelDetail.modelFileId,
          filePath: this.modelDetail.filePath,
        })
        console.log('获取的分配信息', err, res)
        if (!err && res && res.code === 200 && res.data && res.data.engineId) {
          console.log('🚀 预加载， runplayer')
          this.runPlayer(res.data.engineId)
        } else {
          this.runPlayer()
        }
      } catch (error) {
        console.log('❌ getDistributeEngine error', error)
        this.runPlayer()
      }
    },
    // 强制终止连接
    async forceTerminate(id) {
      if (id) {
        const [err, res] = await preloadService.forceTerminate({
          id,
          t: new Date().valueOf(),
        })
      }
    },
    // 监听模型加载进度
    checkModelLoad(data, node, name) {
      const modelList = filter(this.sceneFileList, (item) => {
        return (
          item.code &&
          item.code?.type === 'modelFile' &&
          last(item.code?.filePath.split('.')) === 'pr'
        )
      })
      if (!this.showLoadProgress) {
        this.loadType = '加载中...'
        this.showLoadProgress = true
      }
      if (data >= 1) {
        this.loadedModels.push(name)
      }
      this.loadPercentage = _.round((_.uniq(this.loadedModels).length / modelList.length) * 100, 2)
    },
    // 移动相机到模型
    async moveToModel(node) {
      const aabb = await this.player.Native.NodeProxy.getAabb(node)
      if (aabb) {
        const difCollection = [
          Math.abs(aabb[1][0] - aabb[0][0]),
          Math.abs(aabb[1][1] - aabb[0][1]),
          Math.abs(aabb[1][2] - aabb[0][2]),
        ]
        const distance = max(difCollection)
        // 计算包围盒的中心点
        const aabbCenter = Vec3.multiplyScalar(Vec3.add(aabb[0], aabb[1]), 0.5)
        await this.player.Native.Camera.moveToPos(aabbCenter, node, distance, 2)
      }
    },
    // 加载player引擎
    async runPlayer(engineId) {
      this.preloadEngine = engineId || null
      this.modelEngineId = generateGUID()
      const param = {
        signalServer: `${this.cloudProtocol}://${this.cloudServerPath}`,
        options: {
          engineId: engineId || this.modelEngineId,
          DomType: this.modelSetting.cloudRenderLoadMode === 'img' ? 1 : 0,
          videoDom: document.getElementById('playerEngineId'), // 绑定视频标签
          imgDom: document.getElementById('playerEngineId'), // 绑定视频标签
          userTimeout: 1000 * 60 * 60, // 用户操作超时设置，毫秒
          exitTimeout: 1000 * 60 * 1, // 用户超时后多久断开会话
          maxTimeout: 1000, // 会话超时时间，允许掉线后引擎保存多久，在该时间内可重新连接到会话，否则会创建新的会话
          // openDebug: true,
        },
      }
      const { data } = await this.cloudRender.Engine.loadEngine(param)
      this.loadingEngine = false
      this.player = data.getDataInfo()
      window.player = this.player
      // 实例化完成将实例化后的player传出去
      this.$emit('sendPlayer', this.player)
      this.initPlayer(engineId)
    },
    modelLoadTime() {
      return parseFloat((Math.random() * 0.8 + 0.2).toFixed(2))
    },
    // 初始化player
    async initPlayer(engineId) {
      // 设置点选时其他节点半透明
      this.player.Native.Settings.set('color.pickup.bkAlpha', 'float', '0.5')
      this.player.Native.Settings.set('viewcube.offset.y', 'int', 100)
      const settingsFileUrl = `data://../apps/${this.modelSetting.folderName}/settings.json`
      const settingsData = await this.player.Native.Settings.loadJsonString(settingsFileUrl)
      if (settingsData) {
        const defaultSettings = JSON.parse(settingsData)
        _.forEach(defaultSettings.Show, (item) => {
          this.player.Native.Settings.set(item.name, item.type, item.value)
        })
      }
      if (this.modelDetail.modelFileId && !engineId) {
        console.log('🚀 手动加载 checkTextureFile')
        this.firstLoad = true
        this.checkTextureFile()
      }
      // this.checkTextureFile()
      // 获取并加载偏好设置
      await getPreferenceSettings(true, this.player)
      // 点选监听
      this.player.Native.ModelTree.EventSelectItemChanged.connect(this.watchMsg)
      console.log('模型加载时间', this.modelLoadTime())
    },
    // 检查文件是否存在
    async checkTextureFile() {
      if (this.modelDetail.modelFileId) {
        if (this.modelDetail.materialFileId) {
          const url = `data://../apps/${this.modelSetting.folderName}/models/${this.modelDetail.modelFileId}/${this.modelDetail.materialFileId}.zip`
          const fileInfo = {
            id: this.modelDetail.materialFileId,
            url,
            fileType: 'zip',
          }
          const apiConfig = {
            path: `${
              Wiz.GetApiClientInstance('file-management')?.basePath
            }/api/file-management/files/${this.modelDetail.materialFileId}/download`,
            method: 'GET',
            headers: {
              Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
            },
          }
          const res = await downloadFile(
            this.player,
            fileInfo,
            apiConfig,
            this.progressMsg,
            this.downloadMaterialMsg()
          )
          if (res.type === 0 && res.success) {
            this.checkModelFile()
          } else {
            this.showLoadProgress = true
            this.loadType = '下载中...'
          }
        } else {
          this.checkModelFile()
        }
      }
    },
    // 检查模型是否存在
    async checkModelFile() {
      const fileType = _.last(this.modelDetail.filePath.split('.')) || 'pr'
      const url = this.modelDetail.filePath
      const fileInfo = {
        id: this.modelDetail.modelFileId,
        url,
        fileType,
      }
      const apiConfig = {
        path: `${Wiz.GetApiClientInstance('file-management')?.basePath}/api/file-management/files/${
          this.modelDetail.modelFileId
        }/download`,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${Wiz.AuthHooks.GetToken()}`,
        },
      }
      const res = await downloadFile(
        this.player,
        fileInfo,
        apiConfig,
        this.progressMsg,
        this.downloadMsg
      )
      if (res.type === 0 && res.success) {
        // 有模型就加载模型
        this.addModel()
      } else {
        this.showLoadProgress = true
        this.loadType = '下载中...'
      }
    },
    // 监听下载模型是否成功
    downloadMsg(file, err) {
      this.loadPercentage = 0
      this.showLoadProgress = false
      if (err) {
        this.$message.warning(`下载文件失败，${file}, ${err}`)
      }
      this.addModel()
    },
    // 材质和模型下载完成后，加载模型
    async addModel() {
      this.sceneRootNode = await this.player.Native.NodeProxy.rootNode()
      const fileType = _.last(this.modelDetail.filePath.split('.'))
      let node
      if (fileType === 'pr') {
        node = await this.loadPrModel(
          this.modelDetail.filePath,
          this.modelDetail.modelFileId,
          this.sceneRootNode,
          player
        )
      } else {
        node = await this.loadModel(
          this.modelDetail.filePath,
          this.modelDetail.modelFileId,
          this.sceneRootNode,
          player
        )
      }
      console.log('node', node)
      if (node) {
        this.moveToModel(node)
      }
      this.$emit('loadModelEnd')
    },
    // 加载非Pr模型
    async loadModel(file, name, parentNode, player) {
      const modelNode = await player.Native.NodeProxy.loadModelNode(parentNode, file, name)
      return modelNode
    },
    // 加载PR模型
    async loadPrModel(file, name, parentNode, player) {
      await player.Native.Model.loadModel(name, file)
      const modelInfo = await player.Native.Model.getInfo(name)
      await player.Native.NodeProxy.setNodeParent(modelInfo.nodePtr, parentNode)
      return modelInfo.nodePtr
    },
    // 监听下载模型是否成功
    downloadMaterialMsg() {
      const that = this
      const fun = async (file, err) => {
        that.showLoadProgress = false
        that.loadPercentage = 0
        if (err) {
          that.$message.warning(`下载贴图文件失败，${file}, ${err}`)
        }
        setTimeout(() => {
          that.checkModelFile()
        }, 1500)
      }
      return fun
    },

    // 监听下载进度
    progressMsg(file, bytesReceived, bytesTotal) {
      if (bytesTotal) {
        this.loadPercentage = (bytesReceived / bytesTotal) * 100
      }
    },
    // 模型点选
    watchMsg(data) {
      this.$emit('updateModelNode', data || {})
      localStorage.setItem('currentModelNodeCode', data.code)
    },
  },
}
</script>

<style lang="scss" scoped>
.engineVideo {
  height: calc(100% + 15px);
  object-fit: contain;
}

.engineImg {
  height: calc(100% + 15px);
}

::v-deep .el-loading-mask {
  background-color: #fff !important;
  opacity: 1 !important;
}

.model-view {
  transform: scale(1, 1);
  transform-origin: 'top left';
}
</style>
